/**
 * Ratings History Page
 * Shows ratings and reviews history for current user
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usersAPI } from '../../services/api';
import {
  Star,
  User,
  Calendar,
  MessageSquare,
  Award,
} from 'lucide-react';
import { format } from 'date-fns';

function RatingsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [ratings, setRatings] = useState([]);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    loadRatings();
  }, [user]);

  const loadRatings = async () => {
    if (!user) return;

    try {
      const data = await usersAPI.getUserRatings(user.id);
      setRatings(data.data || []);
      setSummary(data.summary);
    } catch (error) {
      console.error('Failed to load ratings:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < rating ? 'text-gold-500 fill-gold-500' : 'text-gray-300'
        }`}
      />
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-display font-bold text-navy-900">
          My Ratings & Reviews
        </h1>
        <p className="text-navy-600 mt-1">
          See what others have said about you
        </p>
      </div>

      {/* Summary Card */}
      {summary && (
        <div className="card">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="text-center sm:text-left mb-4 sm:mb-0">
              <div className="flex items-center justify-center sm:justify-start">
                <span className="text-4xl font-bold text-navy-900">
                  {summary.average_rating?.toFixed(1) || '0.0'}
                </span>
                <div className="ml-3 flex">
                  {renderStars(Math.round(summary.average_rating || 0))}
                </div>
              </div>
              <p className="text-navy-500 mt-1">
                Based on {summary.total_ratings || 0} review{summary.total_ratings !== 1 ? 's' : ''}
              </p>
            </div>

            {/* Rating Distribution */}
            {summary.distribution && (
              <div className="space-y-1">
                {[5, 4, 3, 2, 1].map((star) => (
                  <div key={star} className="flex items-center text-sm">
                    <span className="w-3 text-navy-600">{star}</span>
                    <Star className="h-3 w-3 text-gold-500 mx-1" />
                    <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden mx-2">
                      <div
                        className="h-full bg-gold-500 rounded-full"
                        style={{
                          width: `${
                            summary.total_ratings
                              ? (summary.distribution[star] / summary.total_ratings) * 100
                              : 0
                          }%`,
                        }}
                      />
                    </div>
                    <span className="w-6 text-navy-500">{summary.distribution[star] || 0}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Ratings List */}
      {ratings.length === 0 ? (
        <div className="card text-center py-12">
          <Award className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-navy-900 mb-2">
            No ratings yet
          </h3>
          <p className="text-navy-600">
            {user?.user_type === 'helper'
              ? 'Complete services to start receiving ratings from yacht owners'
              : 'Rate helpers after completed services to share your experience'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <h2 className="text-lg font-display font-semibold text-navy-900">
            All Reviews ({ratings.length})
          </h2>

          {ratings.map((rating) => (
            <div key={rating.id} className="card">
              <div className="flex items-start justify-between">
                <div className="flex items-center">
                  <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <User className="h-5 w-5 text-primary-600" />
                  </div>
                  <div className="ml-3">
                    <p className="font-medium text-navy-900">
                      {rating.rater_first_name} {rating.rater_last_name}
                    </p>
                    <div className="flex items-center text-sm text-navy-500">
                      <Calendar className="h-3 w-3 mr-1" />
                      {format(new Date(rating.created_at), 'MMM d, yyyy')}
                    </div>
                  </div>
                </div>
                <div className="flex">
                  {renderStars(rating.overall_rating || rating.rating)}
                </div>
              </div>

              {(rating.comment || rating.review_text) && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-start">
                    <MessageSquare className="h-4 w-4 text-navy-400 mt-0.5 mr-2 flex-shrink-0" />
                    <p className="text-navy-700">
                      {rating.comment || rating.review_text}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default RatingsPage;
